'use client';

import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';

type MovieRewardsNavigationRoute = {
  desktopLabel: string;
  href: string;
  label: string;
  matchingPaths?: string[];
};

const movieRewardsNavigationRoutes: MovieRewardsNavigationRoute[] = [
  {
    desktopLabel: 'Movie Rewards',
    href: '/movie-rewards-info',
    label: 'Cinemark Movie Rewards',
  },
  {
    desktopLabel: 'Points & Rewards',
    href: '/movie-rewards-info/points-rewards',
    label: 'Points & Rewards',
  },
  {
    desktopLabel: 'Movie Fan',
    href: '/movie-rewards-info/movie-fan',
    label: 'Movie Fan',
  },
  {
    desktopLabel: 'Movie Club',
    href: '/movieclub',
    label: 'Movie Club',
  },
  {
    desktopLabel: 'Premium Screen Upgrades',
    href: '/premium-screen-upgrade/',
    label: 'Premium Screen Upgrades',
  },
  {
    desktopLabel: 'Platinum',
    href: '/misc/movie-club-misc-landing-pages/movie-club-platinum-lp',
    label: 'Movie Club Platinum',
  },
  {
    desktopLabel: 'FAQs',
    href: '/faq-movierewards',
    label: 'FAQs',
    matchingPaths: [
      '/cinemark-movie-rewards-faq/cinemark-movie-rewards-faq',
      '/cinemark-movie-rewards-faq/movie-club-faq-new',
    ],
  },
];

export const MovieRewardsNavigation = (): React.JSX.Element => {
  const pathname = usePathname();

  return (
    <>
      <nav aria-label="Movie Rewards"
        className="fixed inset-x-0 top-[var(--header-height,95px)] z-90 h-[42px] border-y border-divider bg-surface md:top-[var(--header-height,120px)] md:h-9"
        data-test="navigation-movie-rewards"
      >
        <div className="h-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="mx-auto flex h-full w-max min-w-full md:w-full md:max-w-[1200px] md:justify-center">
            {movieRewardsNavigationRoutes.map((route) => {
              const isActive = pathname === route.href || route.matchingPaths?.includes(pathname) === true;

              return (
                <CinemarkLink key={route.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={clsx(
                    'flex min-w-[75px] max-w-[110px] items-center justify-center border-r border-divider px-[0.6rem]',
                    'text-center text-[11px] font-semibold leading-[12px]',
                    'first:border-l-0 last:border-r-0',
                    'md:min-w-0 md:flex-1 md:px-sm md:max-w-[172px]',
                    isActive ? 'bg-primary text-white' : 'bg-surface text-on-surface-subtle-darker'
                  )}
                  dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
                  dataLinkName={route.label}
                  dataTest={`link-movie-rewards-${dataTestTransformer(route.label)}`}
                  href={route.href}
                >
                  <span className="md:hidden">{route.label}</span>
                  <span className="hidden md:inline">{route.desktopLabel}</span>
                </CinemarkLink>
              );
            })}
          </div>
        </div>
      </nav>
      <div aria-hidden="true"
        className="h-[42px] md:h-9"
      />
    </>
  );
};
